import React, { Fragment } from 'react';
import { Platform, View } from 'react-native';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { BaseText } from '../../common/BaseText';
import { AvatarImage } from '../../common/AvatarImage';
import { Separator } from '../../common/Separator';
import { TextTruncated } from '../../common/TextTruncated';
import { RichText } from '../../common/RichText';
import { parseApiError, passIf } from '@utils';
import { Comment } from '@models/Comments/CommentModel';
import { ToastService } from '@services';
import { ResponsiveIconTouchable } from '../../common/ResponsiveIconTouchable';
import { CommentActionMenu } from './CommentActionMenu';
import { ExtendCommentTouchable } from './ExtendCommentTouchable';
import type { ICommentListStore } from '../CommentList';
import {
  CommentInput,
  CommentInputModeEnum,
  CommentInputSizeEnum,
} from '../CommentInput';
import { User } from '@models/Viewer/UserModel';
import { Touchable, TouchableTypeEnum } from '../../common/Touchable';

const Container = styled(View)({});

const RowContainer = styled(View)({
  flexDirection: 'row',
});

const AuthorHeader = styled(View)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const ContentContainer = styled(View)({
  flex: 1,
  bg: '$background20',
  borderRadius: 8,
  pt: '$4',
  px: '$5',
});

const AuthorFullNameContainer = styled(View)({
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center',
});

const AuthorFullName = styled(BaseText)({
  flexShrink: 1,
  variant: 'text.medium',
  fontWeight: 'semi',
  color: '$heading800',
});

const CommentTime = styled(BaseText)({
  color: '$text100',
  variant: 'text.medium',
});

const MessageText = styled(RichText)({
  color: '$text400',
  variant: 'text.medium',
});

const ActionsContainer = styled(View)({
  flexDirection: 'row',
  flexWrap: 'wrap',
});

export interface ICommentViewProps {
  item: Comment;
  onPressReply(item: Comment): void;
  onPressEdit(item: Comment): void;
  onPressExtend?(): void;
  isChildrenListExtended?: boolean;
  commentListStore: ICommentListStore;
  commentInputSize?: CommentInputSizeEnum;
  onPressUser?(user: User): void;
}

export const CommentView = observer<ICommentViewProps>(
  ({
    item,
    onPressReply,
    onPressEdit,
    onPressExtend,
    isChildrenListExtended = false,
    commentListStore,
    commentInputSize,
    onPressUser,
  }) => {
    const { t } = useTranslation();

    async function toggleLike() {
      try {
        await item.toggleLike.run();
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    function toggleExtend() {
      if (onPressExtend) {
        onPressExtend();
      }
    }

    function innerOnPressUser() {
      if (onPressUser) {
        onPressUser(item.user);
      }
    }

    const likeIconName = item.userStats.isLiked
      ? 'heart-bold'
      : 'heart-light';
    const likesTitle = t('common.commentItem.like', {
      count: item.likes,
    });

    if (commentListStore.isInUpdateMode(item.id)) {
      return (
        <Container>
          <CommentInput
            target={{
              entity: item,
              mode: CommentInputModeEnum.Update,
            }}
            size={commentInputSize}
            onCancel={commentListStore.deleteId}
            onSubmit={commentListStore.deleteId}
          />
        </Container>
      );
    }

    return (
      <Container>
        <RowContainer>
          <Touchable
            onPress={innerOnPressUser}
            disabled={!onPressUser}
            type={Platform.select({
              android: TouchableTypeEnum.WithoutFeedback,
            })}
          >
            <AvatarImage
              source={{ uri: item.user.photo.small?.url }}
              size={36}
            />
          </Touchable>

          <Separator size="$4" vertical />

          <ContentContainer>
            <AuthorHeader>
              <AuthorFullNameContainer>
                <Touchable
                  onPress={innerOnPressUser}
                  disabled={!onPressUser}
                  type={Platform.select({
                    android: TouchableTypeEnum.WithoutFeedback,
                  })}
                >
                  <AuthorFullName numberOfLines={1}>
                    {item.user.fullName}
                  </AuthorFullName>
                </Touchable>
                <Separator size="$4" vertical />
                <CommentTime>|</CommentTime>
                <Separator size="$4" vertical />
                <CommentTime numberOfLines={1}>
                  {item.formattedSentTime}
                </CommentTime>
              </AuthorFullNameContainer>

              <Separator size="$6" vertical />

              <CommentActionMenu
                item={item}
                onPressEdit={onPressEdit}
              />
            </AuthorHeader>

            <Separator size="$2" />

            <TextTruncated numberOfLines={3}>
              <MessageText>{item.message}</MessageText>
            </TextTruncated>

            <Separator size="$5" />

            <ActionsContainer>
              <View>
                <ResponsiveIconTouchable
                  onPress={toggleLike}
                  name={likeIconName}
                  color={passIf(item.userStats.isLiked, '$error300')}
                  title={likesTitle}
                  isResponsiveTitle={false}
                />
                <Separator size="$5" />
              </View>

              {item.canReply && (
                <Fragment>
                  <Separator size={['$6', '$7']} vertical />

                  <View>
                    <ResponsiveIconTouchable
                      onPress={() => onPressReply(item)}
                      name="comment-light"
                      title={t('common.commentItem.reply')}
                      isResponsiveTitle={false}
                    />
                    <Separator size="$5" />
                  </View>
                </Fragment>
              )}

              {item.hasChildren && (
                <Fragment>
                  <Separator size={['$6', '$7']} vertical />

                  <View>
                    <ExtendCommentTouchable
                      onPress={toggleExtend}
                      isExtended={isChildrenListExtended}
                      item={item}
                    />
                    <Separator size="$5" />
                  </View>
                </Fragment>
              )}
            </ActionsContainer>
          </ContentContainer>
        </RowContainer>
      </Container>
    );
  },
);
